import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import AttachmentInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/attachment-interface';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import JobContribution from '@/model/entity/job/job-contribution';
import JobContributionDto from '@/model/entity/job/job-contribution-dto';
import Timesheet from '@/model/entity/audit/timesheet';
import TimesheetDto from '@/model/entity/audit/timesheet-dto';
import TimesheetService from '@/model/service/audit/timesheet-service';

@Component
export default class JobContributionForm extends Vue
{
    @Prop()
    private jobContribution!: JobContribution;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;
    private timesheetVisible = false;

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form: any,
    };

    private created()
    {
        this.generateForm();
    }

    @Watch('jobContribution')
    private onJobContributionChanged()
    {
        this.generateForm();
    }

    /**
     * Handles value changes
     *
     * @param values
     */

    private async onValuesChanged(values: FormFieldValuesInterface)
    {
        if (this.$user?.id && values.addTimesheet === true && !this.timesheetVisible)
        {
            const now = new Date();

            const latestTimesheet = await this.$serviceContainer.timesheetService.getLatestTimesheet(this.$user.id, now);
            const nextDateFrom = await this.$serviceContainer.timesheetService.getNextDateFrom(now, latestTimesheet ? [ latestTimesheet ] : []);

            this.$refs.form.setValue('dateFrom', nextDateFrom);
            this.$refs.form.setValue('dateTo', now);

            this.timesheetVisible = true;
        }
        else if (values.addTimesheet === false)
        {
            this.timesheetVisible = false;
        }

        this.$emit('change:values', values);
    }

    /**
     * Handles form cancels
     */

    private onCancel()
    {
        this.$emit('cancel');
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.jobContribution)
        {
            return;
        }

        // Generate a job contribution from the form data
        const jobContribution = this.generateJobContributionFromForm(form);

        // Generate a timesheet from the form data
        let timesheet: Timesheet|null;

        if (this.addTimesheet(form))
        {
            timesheet = this.generateTimesheetFromForm(form);
        }
        else
        {
            timesheet = null;
        }

        // Emit a save event with the job contribution
        this.$emit(
            'save',
            jobContribution,
            timesheet,
        );
    }

    /**
     * Generates the form
     */

    private generateForm()
    {
        if (!this.jobContribution)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'notes',
                    type: 'string',
                    title: this.$t('general.field.notes').toString(),
                    hideTitle: true,
                    value: this.jobContribution.notes,
                    required: true,
                    multiline: true,
                    rich: true,
                    markdown: true,
                },
                {
                    key: 'attachment1',
                    type: 'file',
                    title: this.$t('job.field.attachment').toString(),
                    value: this.jobContribution.attachment1,
                    required: false,
                    emitDownload: true,
                },
                {
                    key: 'clientVisible',
                    type: 'boolean',
                    title: this.$t('job.field.client_visible').toString(),
                    value: this.jobContribution.clientVisible,
                    required: true,
                },
                {
                    key: 'addTimesheet',
                    type: 'boolean',
                    title: this.$t('job.field.add_timesheet').toString(),
                    value: false,
                    required: true,
                },
                {
                    key: 'dateFrom',
                    type: 'datetime',
                    title: this.$t('general.field.from').toString(),
                    value: null,
                    required: true,
                    dependencies: {
                        addTimesheet: true,
                    },
                },
                {
                    key: 'dateTo',
                    type: 'datetime',
                    title: this.$t('general.field.to').toString(),
                    value: null,
                    required: true,
                    dependencies: {
                        addTimesheet: true,
                    },
                    validator: (field, fieldValues): string[] =>
                    {
                        const errors: string[] = [];

                        if (field.value && fieldValues.dateFrom && field.value <= fieldValues.dateFrom)
                        {
                            errors.push(this.$t('timesheet.error.must_be_after_from').toString());
                        }

                        return errors;
                    },
                },
                {
                    key: 'chargeable',
                    type: 'string',
                    title: this.$t('timesheet.field.chargeable').toString(),
                    value: null,
                    required: true,
                    dependencies: {
                        addTimesheet: true,
                    },
                    options: TimesheetService.chargeableOptions,
                    dropdown: true,
                },
                {
                    key: 'activity',
                    type: 'string',
                    title: this.$t('timesheet.field.activity').toString(),
                    value: null,
                    required: true,
                    dependencies: {
                        addTimesheet: true,
                    },
                    options: TimesheetService.activityOptions,
                    dropdown: true,
                },
            ],
            cancelButton: true,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the job contribution from the form
     */

    private generateJobContributionFromForm(form: FormInterface): JobContribution
    {
        if (!this.jobContribution)
        {
            return new JobContribution(new JobContributionDto());
        }

        const jobContribution = new JobContribution(cloneDeep(this.jobContribution.dto));
        const timesheet = new Timesheet(new TimesheetDto());
        timesheet.jobId = jobContribution.jobId;

        this.addJobContributionFieldValues(jobContribution, form.items);

        return jobContribution;
    }

    /**
     * Generates the timesheet from the form
     */

    private generateTimesheetFromForm(form: FormInterface): Timesheet
    {
        const timesheet = new Timesheet(new TimesheetDto());
        timesheet.jobId = this.jobContribution.jobId;

        this.addTimesheetFieldValues(timesheet, form.items);

        return timesheet;
    }

    /**
     * Adds field values from form items to the passed job contribution
     *
     * @param jobContribution
     * @param items
     */

    private addJobContributionFieldValues(jobContribution: JobContribution, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addJobContributionFieldValues(jobContribution, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'notes':
                jobContribution.notes = item.value as string;
                break;

            case 'attachment1':
                jobContribution.attachment1 = item.value as AttachmentInterface;
                break;

            case 'clientVisible':
                jobContribution.clientVisible = item.value as boolean;
                break;
            }
        }
    }

    /**
     * Adds field values from form items to the passed timesheet
     *
     * @param timesheet
     * @param items
     */

    private addTimesheetFieldValues(timesheet: Timesheet, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addTimesheetFieldValues(timesheet, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'dateFrom':
                timesheet.dateFrom = item.value as Date;
                break;

            case 'dateTo':
                timesheet.dateTo = item.value as Date;
                break;

            case 'chargeable':
                timesheet.chargeable = item.value as string;
                break;

            case 'activity':
                timesheet.activity = item.value as string;
                break;
            }
        }
    }

    /**
     * Returns whether a timesheet should be added as well
     *
     * @param form
     * @returns
     */

    private addTimesheet(form: FormInterface): boolean
    {
        for (const item of form.items)
        {
            const field = item as FormFieldInterface;

            if (field.key === 'addTimesheet')
            {
                return field.value as boolean;
            }
        }

        return false;
    }

    /**
     * Handles file download requests
     *
     * @param attachment
     */

    private onDownload(attachment: AttachmentInterface)
    {
        this.$serviceContainer.jobService.downloadAttachment(attachment);
    }
}